import { router } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";

type PhotoEvent = {
  id: number;
  name: string;
  place: number;
  in_bounty: boolean;
  organizer_email: string | null;
  first_at: string | null;
  photos: string[];
  link: string | null;
  board_path: string;
};

type Recipient = { id: number; name: string; email: string | null };

function when(iso: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminPhotos({
  events,
  bounty_places,
  recipients,
  already_emailed,
}: {
  events: PhotoEvent[];
  bounty_places: number;
  recipients: Recipient[];
  already_emailed: number;
}) {
  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Event photos"
          subtitle={`Photos organizers sent after the season, in the order their first one arrived. The first ${bounty_places} cities get a $5 grant.`}
        />

        <Panel title="photo-email.txt">
          <div className="flex flex-col gap-3">
            <p className="text-body text-ink-muted">
              {recipients.length > 0
                ? `${recipients.length} cities that held their event haven't been asked yet. This emails each point of contact once.`
                : "Every city that held its event has been asked."}{" "}
              {already_emailed > 0 && `${already_emailed} already emailed.`}
            </p>
            {recipients.length > 0 && (
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Email ${recipients.length} organizers asking for photos?`,
                      )
                    )
                      router.post("/admin/photos/announce");
                  }}
                >
                  Email {recipients.length} organizers
                </Button>
              </div>
            )}
          </div>
        </Panel>

        {events.length === 0 ? (
          <EmptyState
            title="No photos yet"
            description="They show up here, first sender at the top, as soon as an organizer uploads some."
          />
        ) : (
          <div className="flex flex-col gap-5">
            {events.map((event) => (
              <Panel key={event.id} title={`${event.place}.txt`}>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={event.board_path}
                      className="font-display text-heading text-ink font-bold underline"
                    >
                      {event.name}
                    </a>
                    {event.in_bounty ? (
                      <Badge tone="success">Gets $5</Badge>
                    ) : (
                      <Badge tone="neutral">
                        After the first {bounty_places}
                      </Badge>
                    )}
                    <span className="text-caption text-ink-soft">
                      first photo {when(event.first_at)} ·{" "}
                      {event.organizer_email}
                    </span>
                  </div>
                  {event.link && (
                    <a
                      href={event.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-link text-body break-all underline"
                    >
                      {event.link}
                    </a>
                  )}
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {event.photos.map((src) => (
                      <a key={src} href={src} target="_blank" rel="noreferrer">
                        <img
                          src={src}
                          alt=""
                          loading="lazy"
                          className="border-line aspect-square w-full border object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
